// Общая служба голоса клиента.
//
// Зачем она есть: тракт обработки должен пережить закрытие экрана. Игрок
// настроил микрофон, закрыл окно и пошёл играть — очистка обязана остаться.
// Поэтому VoiceChain живёт здесь, в единственном экземпляре на весь клиент,
// а экраны и LiveKit только берут его напрямую.
//
// Микрофон держим ровно столько, сколько нужно: считаем захваты (acquire /
// release). Ноль захватов — отпускаем устройство, чтобы браузер убрал
// индикатор записи и не жёг батарею.
//
// Контракт с ядром грида (ClientLiveKit) намеренно узкий — ядро не знает
// ни про Web Audio, ни про экраны:
//   getTrack(): Promise<MediaStreamTrack|null>
//   isEnabled(): boolean
//   on('track'|'change', handler) / off(...)

// расширение обязательно: этот модуль читает и Node (тесты), а не только сборщик
import { VoiceChain, loadVoiceSettings, saveVoiceSettings, normalizeSettings } from './voice-chain.js'
import { ENHANCED_VOICE_FEATURE, loadPublishingConsent, savePublishingConsent } from './voice-publication.js'

export class VoiceService {
  constructor() {
    const stored = loadVoiceSettings()
    this.chain = new VoiceChain()
    this.settings = stored.settings
    this.enabled = stored.enabled
    this.publishingFeature = ENHANCED_VOICE_FEATURE.enabled === true
    this.publishingConsent = loadPublishingConsent()
    this.deviceId = stored.deviceId
    this.capture = stored.capture
    this.holders = new Set()
    this.starting = null
    this.error = null
    this.handlers = { track: new Set(), change: new Set() }
  }

  // --- события ---

  on(event, handler) {
    this.handlers[event]?.add(handler)
    return () => this.off(event, handler)
  }

  off(event, handler) {
    this.handlers[event]?.delete(handler)
  }

  emit(event, payload) {
    this.handlers[event]?.forEach(handler => {
      try {
        handler(payload)
      } catch (error) {
        console.error('[voice] handler failed', error)
      }
    })
  }

  // --- владение микрофоном ---

  // Кто-то начал пользоваться голосом (экран или включённый микрофон).
  async acquire(holder) {
    this.holders.add(holder)
    return this.ensure()
  }

  // Отпустили. Когда владельцев не осталось — закрываем захват.
  release(holder) {
    this.holders.delete(holder)
    if (this.holders.size === 0) this.stop()
  }

  get running() {
    return this.chain.ready
  }

  async ensure() {
    if (this.chain.ready) return this.chain
    if (this.starting) return this.starting
    this.starting = this.chain
      .start({ deviceId: this.deviceId, settings: this.settings, capture: this.capture })
      .then(() => {
        this.chain.setBypass(!this.enabled)
        this.error = null
        this.starting = null
        // трек сменился — тому, кто его публикует, надо переопубликовать
        this.emit('track', this.track)
        this.emit('change', this)
        return this.chain
      })
      .catch(error => {
        this.starting = null
        this.error = error?.name === 'NotAllowedError' ? 'Доступ к микрофону запрещён в браузере.' : String(error?.message || error)
        this.emit('change', this)
        throw error
      })
    return this.starting
  }

  stop() {
    if (!this.chain.ready && !this.starting) return
    this.chain.stop()
    this.emit('track', null)
    this.emit('change', this)
  }

  // --- настройки ---

  update(settings) {
    this.settings = normalizeSettings(settings)
    this.chain.update(this.settings)
    this.emit('change', this)
    return this.settings
  }

  setEnabled(flag) {
    this.enabled = !!flag
    this.chain.setBypass(!this.enabled)
    this.emit('track', this.track)
    this.emit('change', this)
    return this.enabled
  }

  // Only this user-action method grants publication consent. Programmatic preset,
  // restore and diagnostics changes must use setEnabled() and cannot opt the player in.
  setPublishingEnabledByUser(flag) {
    const next = flag === true
    this.enabled = next
    this.publishingConsent = next
    this.chain.setBypass(!next)
    savePublishingConsent(next)
    this.emit('track', this.track)
    this.emit('change', this)
    return this.isPublishingEnabled()
  }

  setPublishingConsent(flag) {
    this.publishingConsent = flag === true
    savePublishingConsent(this.publishingConsent)
    this.emit('track', this.track)
    this.emit('change', this)
    return this.publishingConsent
  }

  async setDevice(deviceId) {
    if (deviceId === this.deviceId && this.chain.ready) return
    this.deviceId = deviceId || ''
    if (!this.chain.ready) return
    // смена устройства пересобирает граф — трек станет новым
    await this.chain.setDevice(this.deviceId)
    this.chain.setBypass(!this.enabled)
    this.emit('track', this.track)
    this.emit('change', this)
  }

  async setCapture(capture) {
    this.capture = await this.chain.setCapture({ ...this.capture, ...capture })
    this.emit('change', this)
    return this.capture
  }

  save() {
    return saveVoiceSettings({
      settings: this.settings,
      enabled: this.enabled,
      deviceId: this.deviceId,
      capture: this.capture,
    })
  }

  // --- контракт для LiveKit ---

  get track() {
    return this.chain.enhancedStream?.getAudioTracks?.()[0] || null
  }

  isEnabled() {
    return this.enabled
  }

  isPublishingEnabled() {
    return this.publishingFeature === true && this.publishingConsent === true && this.enabled === true
  }

  // LiveKit просит трек для публикации. Если обработка не поднялась —
  // честно отдаём null, и ядро уходит на штатный микрофон: игрок не должен
  // остаться немым из-за нашей очистки.
  async getTrack() {
    if (!this.isPublishingEnabled()) return null
    try {
      await this.acquire('livekit')
      const track = this.track
      if (!track) this.release('livekit')
      return track
    } catch {
      this.release('livekit')
      return null
    }
  }

  releaseTrack() {
    this.release('livekit')
  }
}

let instance = null

export function getVoiceService() {
  if (!instance) instance = new VoiceService()
  return instance
}

// Подключение к миру: вызывается один раз при создании клиента.
export function attachVoiceService(world) {
  const service = getVoiceService()
  if (!world?.livekit?.setMicProcessor) return service
  world.livekit.setMicProcessor(service)
  return service
}
